import React, { useCallback, useEffect, useState } from 'react';
import pluralize from 'pluralize';
import { Button } from 'antd';

import AccountNumberModal from './AccountNumberModal';
import UnprocessedPaymentsTable from './UnprocessedPaymentsTable';
import ACHPaymentsUploaderModal from './ACHPaymentsUploaderModal';
import { ACHPaymentsPagedResponseModel } from 'api/adminApiClient';
import { getACHUnprocessedPayments } from 'redux/reducers/achPaymentsSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';

const DEFAULT_TABLE_PAGE = 1;

export default function UnprocessedPaymentsSection() {
  const dispatch = useAppDispatch();
  const [isAccountNumberModalOpen, setIsAccountNumberModalOpen] = useState(
    false,
  );
  const [
    isACHPaymentsUploaderModalOpen,
    setIsACHPaymentsUploaderModalOpen,
  ] = useState(false);
  const [currentTablePage, setCurrentTablePage] = useState(DEFAULT_TABLE_PAGE);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const [
    isUnprocessedPaymenetsDataLoading,
    setIsUnprocessedPaymenetsDataLoading,
  ] = useState(false);
  const [
    unprocessedPaymenetsData,
    setUnprocessedPaymenetsData,
  ] = useState<ACHPaymentsPagedResponseModel>();
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);

  const loadUnprocessedPayments = useCallback(
    async (page: number) => {
      setIsUnprocessedPaymenetsDataLoading(true);

      const result = await dispatch(getACHUnprocessedPayments(page - 1));

      if (getACHUnprocessedPayments.fulfilled.match(result)) {
        setUnprocessedPaymenetsData(result.payload);
      } else {
        setUnprocessedPaymenetsData({
          totalCount: 0,
          details: [],
        });
      }

      setIsUnprocessedPaymenetsDataLoading(false);
    },
    [dispatch],
  );

  const loadInitialData = useCallback(async () => {
    await loadUnprocessedPayments(DEFAULT_TABLE_PAGE);

    setIsInitialDataLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = async () => {
    setSelectedPaymentIds([]);
    setCurrentTablePage(DEFAULT_TABLE_PAGE);
    await loadUnprocessedPayments(DEFAULT_TABLE_PAGE);
  };

  const onPageChange = (page: number) => {
    setCurrentTablePage(page);
    loadUnprocessedPayments(page);
  };

  const handleAddAccountNumberClick = () => {
    setIsAccountNumberModalOpen(true);
  };

  const handleUploadPaymentsClick = () => {
    setIsACHPaymentsUploaderModalOpen(true);
  };

  const unprocessedPaymenetsCount = pluralize(
    'payment',
    unprocessedPaymenetsData?.totalCount,
    true,
  );

  return (
    <>
      {isACHPaymentsUploaderModalOpen && (
        <ACHPaymentsUploaderModal
          onPaymentsUploaded={reset}
          onClose={() => setIsACHPaymentsUploaderModalOpen(false)}
        />
      )}

      {isAccountNumberModalOpen && (
        <AccountNumberModal
          onAccNumberAdded={reset}
          selectedPaymentIds={selectedPaymentIds}
          onClose={() => setIsAccountNumberModalOpen(false)}
        />
      )}

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <div className="page-head">
            <div className="page-head__left">
              <div className="page-title page-head__title">
                PAYMENTS TO BE PROCESSED
                {isInitialDataLoaded && (
                  <div className="page-head__subtitle">
                    {unprocessedPaymenetsCount}
                  </div>
                )}
              </div>
            </div>

            <div className="page-head__actions">
              <div>
                <Button
                  size="middle"
                  disabled={!selectedPaymentIds.length}
                  onClick={handleAddAccountNumberClick}
                >
                  Add account number
                </Button>
              </div>

              <div>
                <Button size="middle" onClick={handleUploadPaymentsClick}>
                  Upload payments
                </Button>
              </div>
            </div>
          </div>

          <UnprocessedPaymentsTable
            currentPage={currentTablePage}
            onPageChange={onPageChange}
            paymenetsData={unprocessedPaymenetsData}
            onSelectPayment={setSelectedPaymentIds}
            selectedPaymentIds={selectedPaymentIds}
            isLoading={isUnprocessedPaymenetsDataLoading}
          />
        </div>
      </div>
    </>
  );
}
